/*!
 * mm-grid.js — from Beat This! activations to a BEAT GRID a mashup can be
 * built on: every beat of the song in seconds (sub-frame precise), which of
 * them start a bar, the metre, and a tempo that is a measurement rather than a
 * rounded frame count.
 *
 * Why not use the model's own peak list (beat_this postp_minimal): its times are
 * whole 20 ms frames, and a song is warped onto another one beat by beat — a
 * ±10 ms jitter per anchor is audible as a flam between the singer and the kick.
 * The peaks also carry the model's local mistakes (a doubled beat in a busy
 * bridge, a missing one in a breakdown). A dynamic-programming pass over the
 * activation with a tempo prior keeps one consistent beat level through the
 * whole song, fills the gaps and drops the doubles; each chosen beat is then
 * placed between frames by a parabola through the three activation samples.
 *
 * Bars: the downbeat activation is summed per bar phase inside sliding windows
 * and a small Viterbi picks the phase path, so a song that inserts a 2/4 bar
 * (very common before a chorus) re-locks instead of drifting a beat off for the
 * rest of the song.
 *
 *   MashupGrid.fit(beatLogits, downLogits, { fps: 50 }) → {
 *       beats: Float64Array (s), downIdx: Int32Array (indices into beats that start a bar),
 *       meter, bpm, bpmStable (0…1), confidence (0…1), downContrast, peaks: n }
 *
 * Pure functions; runs in the analysis worker and under node.
 */
(function (root) {
    'use strict';

    function sigmoid(x) { return 1 / (1 + Math.exp(-x)); }

    /** Local maxima within ±3 frames with logit > 0 (the model's own rule). */
    function peakFrames(logits) {
        var n = logits.length, out = [];
        for (var i = 0; i < n; i++) {
            var v = logits[i];
            if (!(v > 0)) continue;
            var ok = true;
            for (var k = -3; k <= 3 && ok; k++) {
                var j = i + k;
                if (k !== 0 && j >= 0 && j < n && logits[j] > v) ok = false;
            }
            if (ok && (!out.length || i - out[out.length - 1] > 1)) out.push(i);
        }
        return out;
    }

    /** Parabolic sub-frame position of a maximum at integer frame f. */
    function refine(sig, f) {
        if (f <= 0 || f >= sig.length - 1) return f;
        var a = sig[f - 1], b = sig[f], c = sig[f + 1], den = a - 2 * b + c;
        if (!(den < 0)) return f;
        var d = 0.5 * (a - c) / den;
        if (d > 0.5) d = 0.5; else if (d < -0.5) d = -0.5;
        return f + d;
    }

    /** Dominant inter-beat period (frames) from peak spacing: log-domain kernel density. */
    function dominantPeriod(peaks, fps) {
        var minP = fps * 60 / 220, maxP = fps * 60 / 40, iv = [];
        for (var i = 1; i < peaks.length; i++) {
            var d = peaks[i] - peaks[i - 1];
            if (d >= minP && d <= maxP) iv.push(d);
        }
        if (iv.length < 4) return null;
        var best = null, bestW = -1;
        for (var c = 0; c < iv.length; c++) {
            var w = 0, lc = Math.log(iv[c]);
            for (var j = 0; j < iv.length; j++) { var z = (Math.log(iv[j]) - lc) / 0.03; w += Math.exp(-0.5 * z * z); }
            if (w > bestW) { bestW = w; best = iv[c]; }
        }
        // mean of the intervals inside ±6 % of the mode — a sub-frame period
        var s = 0, m = 0;
        for (j = 0; j < iv.length; j++) if (Math.abs(iv[j] / best - 1) < 0.06) { s += iv[j]; m++; }
        return m ? s / m : best;
    }

    /**
     * Ellis-style dynamic programming over a frame-wise score with a log-period
     * prior around `period`. Returns beat frames (integers).
     */
    function dpBeats(score, period, tightness) {
        var n = score.length, back = new Int32Array(n).fill(-1), cum = new Float64Array(n);
        var lo = Math.max(1, Math.round(period * 0.5)), hi = Math.round(period * 2);
        var txwt = new Float64Array(hi + 1);
        for (var d = lo; d <= hi; d++) { var l = Math.log(d / period); txwt[d] = -tightness * l * l; }
        for (var i = 0; i < n; i++) {
            var best = 0, bi = -1;
            for (d = lo; d <= hi && i - d >= 0; d++) {
                var v = cum[i - d] + txwt[d];
                if (bi < 0 || v > best) { best = v; bi = i - d; }
            }
            cum[i] = score[i] + (bi >= 0 ? Math.max(0, best) : 0);
            back[i] = (bi >= 0 && best > 0) ? bi : -1;
        }
        // end at the best-scoring frame in the last period that still sits on a beat
        var end = -1, endV = -Infinity;
        for (i = Math.max(0, n - hi); i < n; i++) if (score[i] > 0.1 && cum[i] > endV) { endV = cum[i]; end = i; }
        if (end < 0) { for (i = Math.max(0, n - hi); i < n; i++) if (cum[i] > endV) { endV = cum[i]; end = i; } }
        var path = [];
        for (i = end; i >= 0; i = back[i]) path.push(i);
        path.reverse();
        // trim a leading/trailing run of beats the model never believed in (silent intro/outro)
        var a = 0, z = path.length - 1;
        while (a < z && score[path[a]] < 0.2) a++;
        while (z > a && score[path[z]] < 0.2) z--;
        return path.slice(a, z + 1);
    }

    /**
     * Bars: downbeat probability sampled at each beat; for every candidate metre
     * a phase path chosen by Viterbi over sliding evidence, then the metre whose
     * on-phase / off-phase contrast is largest.
     */
    function barsFor(dprob, meter) {
        var n = dprob.length, S = meter;
        if (n < meter * 2) return { downIdx: [], contrast: 0 };
        // per-beat emission for state s (= the phase of beat 0 inside the bar)
        var switchPen = 2.5, dp = new Float64Array(S), next = new Float64Array(S), bp = [];
        for (var s = 0; s < S; s++) dp[s] = 0;
        for (var j = 0; j < n; j++) {
            var row = new Int8Array(S);
            var bestPrev = -Infinity, bestPrevS = 0;
            for (s = 0; s < S; s++) if (dp[s] > bestPrev) { bestPrev = dp[s]; bestPrevS = s; }
            for (s = 0; s < S; s++) {
                var stay = dp[s], sw = bestPrev - switchPen, from = s, v = stay;
                if (sw > stay && bestPrevS !== s) { v = sw; from = bestPrevS; }
                var isDown = ((j - s) % S + S) % S === 0;
                // log-likelihood ratio of "downbeat here" vs "not"
                var p = Math.min(0.999, Math.max(0.001, dprob[j]));
                next[s] = v + (isDown ? Math.log(p / 0.25) : Math.log((1 - p) / 0.75));
                row[s] = from;
            }
            bp.push(row);
            var t = dp; dp = next; next = t;
        }
        var cur = 0, bv = -Infinity;
        for (s = 0; s < S; s++) if (dp[s] > bv) { bv = dp[s]; cur = s; }
        var phase = new Int8Array(n);
        for (j = n - 1; j >= 0; j--) { phase[j] = cur; cur = bp[j][cur]; }
        var downIdx = [], on = 0, onN = 0, off = 0, offN = 0;
        for (j = 0; j < n; j++) {
            if (((j - phase[j]) % S + S) % S === 0) { downIdx.push(j); on += dprob[j]; onN++; }
            else { off += dprob[j]; offN++; }
        }
        // a bar can only restart on a beat that is ≥ 2 beats after the previous bar start
        var clean = [];
        for (var k = 0; k < downIdx.length; k++) {
            if (!clean.length || downIdx[k] - clean[clean.length - 1] >= 2) clean.push(downIdx[k]);
        }
        return { downIdx: clean, contrast: (onN ? on / onN : 0) - (offN ? off / offN : 0) };
    }

    function linearTempo(beats) {
        // least squares of t_j = a + b·j over the longest stretch whose local IBI stays within ±4 % of the median
        var n = beats.length;
        if (n < 8) return null;
        var ibi = [], i;
        for (i = 1; i < n; i++) ibi.push(beats[i] - beats[i - 1]);
        var sorted = ibi.slice().sort(function (a, b) { return a - b; }), med = sorted[sorted.length >> 1];
        var sx = 0, sy = 0, sxx = 0, sxy = 0, m = 0, good = 0;
        for (i = 0; i < n; i++) {
            var okL = i === 0 || Math.abs(ibi[i - 1] / med - 1) < 0.04, okR = i === n - 1 || Math.abs(ibi[i] / med - 1) < 0.04;
            if (!(okL && okR)) continue;
            sx += i; sy += beats[i]; sxx += i * i; sxy += i * beats[i]; m++;
        }
        for (i = 0; i < ibi.length; i++) if (Math.abs(ibi[i] / med - 1) < 0.04) good++;
        if (m < 8) return { period: med, stable: good / ibi.length };
        var b = (m * sxy - sx * sy) / (m * sxx - sx * sx);
        return { period: b > 0 ? b : med, stable: good / ibi.length, median: med };
    }

    function fit(beatLogits, downLogits, opts) {
        opts = opts || {};
        var fps = opts.fps || 50, n = beatLogits.length, i;
        var bprob = new Float32Array(n), dprobF = new Float32Array(n);
        for (i = 0; i < n; i++) { bprob[i] = sigmoid(beatLogits[i]); dprobF[i] = sigmoid(downLogits[i]); }
        var peaks = peakFrames(beatLogits);
        var period = dominantPeriod(peaks, fps);
        if (!period) return { beats: new Float64Array(0), downIdx: new Int32Array(0), meter: 4, bpm: 0, bpmStable: 0, confidence: 0, downContrast: 0, peaks: peaks.length };
        var frames = dpBeats(bprob, period, opts.tightness || 400);
        var beats = new Float64Array(frames.length), atBeat = new Float32Array(frames.length), hit = 0;
        var pk = new Uint8Array(n); for (i = 0; i < peaks.length; i++) pk[peaks[i]] = 1;
        for (i = 0; i < frames.length; i++) {
            var f = frames[i];
            // snap to the local activation maximum (±2 frames) before refining
            var bf = f;
            for (var k = -2; k <= 2; k++) { var g = f + k; if (g >= 0 && g < n && bprob[g] > bprob[bf]) bf = g; }
            beats[i] = refine(beatLogits, bf) / fps;
            atBeat[i] = bprob[bf];
            if (pk[bf] || (bf > 0 && pk[bf - 1]) || (bf < n - 1 && pk[bf + 1])) hit++;
        }
        // downbeat probability at each beat (max over ±1 frame)
        var dAt = new Float32Array(frames.length);
        for (i = 0; i < frames.length; i++) {
            var fr = Math.round(beats[i] * fps), mx = 0;
            for (k = -1; k <= 1; k++) { var q = fr + k; if (q >= 0 && q < n && dprobF[q] > mx) mx = dprobF[q]; }
            dAt[i] = mx;
        }
        var b4 = barsFor(dAt, 4), b3 = barsFor(dAt, 3);
        var bars = (b3.contrast > b4.contrast * 1.25 + 0.03) ? { r: b3, meter: 3 } : { r: b4, meter: 4 };
        var lt = linearTempo(beats);
        var bpm = lt ? 60 / (lt.period) : 60 * fps / period;
        var conf = 0; for (i = 0; i < atBeat.length; i++) conf += atBeat[i];
        conf = atBeat.length ? conf / atBeat.length : 0;
        return {
            beats: beats, downIdx: Int32Array.from(bars.r.downIdx), meter: bars.meter,
            bpm: Math.round(bpm * 100) / 100, bpmStable: lt ? Math.round(lt.stable * 1000) / 1000 : 0,
            confidence: Math.round(conf * 1000) / 1000, support: frames.length ? Math.round(hit / frames.length * 1000) / 1000 : 0,
            downContrast: Math.round(bars.r.contrast * 1000) / 1000, peaks: peaks.length
        };
    }

    /**
     * A grid from a TEMPO alone (acapella or a file the model could not read):
     * constant beats from `offsetSec` at `bpm` across `durationSec`, bars every
     * `meter` beats from beat 0.
     */
    function constantGrid(bpm, offsetSec, durationSec, meter) {
        meter = meter || 4;
        var p = 60 / bpm, n = Math.max(0, Math.floor((durationSec - offsetSec) / p) + 1);
        var beats = new Float64Array(n), down = [];
        for (var i = 0; i < n; i++) { beats[i] = offsetSec + i * p; if (i % meter === 0) down.push(i); }
        return { beats: beats, downIdx: Int32Array.from(down), meter: meter, bpm: bpm, bpmStable: 1, confidence: 0, support: 0, downContrast: 0, peaks: 0, manual: true };
    }

    /**
     * A VOICE ALONE (a cappella): the model hears syllables, not a drummer — beat
     * probabilities stay under 0.2 almost everywhere, and fit() trims its path to a
     * handful of beats (a real a cappella: 117.9 BPM, two bars, and the plan refused
     * the song). A studio a cappella keeps the constant tempo of the session it came
     * from, so over three minutes even faint syllable onsets add up along ONE comb.
     *
     * combScore(prob, fps, bpm) → { score, phase }: the best phase's mean probability
     * along a constant comb at `bpm` (linear interpolation between frames).
     */
    function combScore(prob, fps, bpm) {
        var n = prob.length, P = fps * 60 / bpm, steps = Math.max(16, Math.round(P * 4));
        var best = -1, bph = 0;
        for (var s = 0; s < steps; s++) {
            var ph = s * P / steps, sum = 0, m = 0;
            for (var x = ph; x < n - 1; x += P) { var i = x | 0, f = x - i; sum += prob[i] * (1 - f) + prob[i + 1] * f; m++; }
            var v = m ? sum / m : 0;
            if (v > best) { best = v; bph = ph; }
        }
        return { score: best, phase: bph / fps };
    }

    /**
     * combTempo(beatLogits, fps) → { bpm, phase, contrast, runnerUp } — the tempo of a
     * file with NO drummer, and how sure the comb is. Measured 2026-09-24 on the corpus'
     * separated vocals against the songs' tagged tempi (octave-agnostic, 12 songs):
     * right octave class 8/12, where fit() found no grid at all on 3 and a wrong one on 3.
     * Precision is NOT that of a drummer's grid (79.75 against a tagged 80) — which is
     * why an a cappella without a tempo in its name or tags asks the person to check.
     * Contrast = the comb's score minus the mean of its ±3…15 % neighbours: a peak, not
     * a plateau.
     */
    function combTempo(beatLogits, fps, opts) {
        opts = opts || {};
        var lo = opts.lo || 55, hi = opts.hi || 200, n = beatLogits.length, i;
        var prob = new Float32Array(n);
        for (i = 0; i < n; i++) prob[i] = sigmoid(beatLogits[i]);
        var cands = [];
        for (var bpm = lo; bpm <= hi + 1e-9; bpm += 0.1) { var c = combScore(prob, fps, bpm); cands.push({ bpm: bpm, score: c.score, phase: c.phase }); }
        if (!cands.length) return null;
        for (i = 0; i < cands.length; i++) {
            var s = 0, m = 0, ci = cands[i];
            for (var j = 0; j < cands.length; j++) { var r = Math.abs(cands[j].bpm / ci.bpm - 1); if (r > 0.03 && r < 0.15) { s += cands[j].score; m++; } }
            ci.contrast = ci.score - (m ? s / m : 0);
        }
        var sorted = cands.slice().sort(function (a, b) { return b.contrast - a.contrast; });
        var top = sorted[0];
        // fine search ±0.3 % in 0.01 BPM steps: the coarse 0.1 grid alone leaves 0.1 % of drift
        var fine = top;
        for (var b2 = top.bpm * 0.997; b2 <= top.bpm * 1.003; b2 += 0.01) {
            var c2 = combScore(prob, fps, b2);
            if (c2.score > fine.score) fine = { bpm: b2, score: c2.score, phase: c2.phase, contrast: top.contrast };
        }
        var runner = null;
        for (i = 1; i < sorted.length; i++) { var q = sorted[i].bpm / top.bpm; if (Math.abs(q - 1) > 0.05) { runner = sorted[i]; break; } }
        return { bpm: Math.round(fine.bpm * 100) / 100, phase: fine.phase, score: fine.score, contrast: top.contrast,
                 runnerUp: runner ? Math.round(runner.bpm * 10) / 10 : null };
    }

    /** The phase of a KNOWN tempo (file name, tags, the person's taps) on a file's own activations. */
    function combPhase(beatLogits, fps, bpm) {
        var n = beatLogits.length, prob = new Float32Array(n);
        for (var i = 0; i < n; i++) prob[i] = sigmoid(beatLogits[i]);
        return combScore(prob, fps, bpm).phase;
    }

    /**
     * Tap tempo: the median interval of the taps (s, performance.now()/1000 or audio time)
     * and their phase — each tap is a beat. ≥ 4 taps; the last 12 count.
     */
    function tapTempo(taps) {
        if (!taps || taps.length < 4) return null;
        var t = taps.slice(-12), iv = [];
        for (var i = 1; i < t.length; i++) iv.push(t[i] - t[i - 1]);
        var s = iv.slice().sort(function (a, b) { return a - b; }), med = s[s.length >> 1];
        if (!(med > 0.25 && med < 1.6)) return null;       // 37…240 BPM
        // least squares over the taps on the median grid — the tempo and where the beats fall
        var sx = 0, sy = 0, sxx = 0, sxy = 0, m = 0;
        for (i = 0; i < t.length; i++) { var k = Math.round((t[i] - t[0]) / med); sx += k; sy += t[i]; sxx += k * k; sxy += k * t[i]; m++; }
        var den = m * sxx - sx * sx, p = den > 0 ? (m * sxy - sx * sy) / den : med;
        if (!(p > 0.25 && p < 1.6)) p = med;
        var a = (sy - p * sx) / m;
        return { bpm: Math.round(6000 / p) / 100, period: p, anchor: a };
    }

    var api = { fit: fit, constantGrid: constantGrid, peakFrames: peakFrames, dpBeats: dpBeats, dominantPeriod: dominantPeriod, barsFor: barsFor, linearTempo: linearTempo, refine: refine,
                combTempo: combTempo, combPhase: combPhase, combScore: combScore, tapTempo: tapTempo };
    root.MashupGrid = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : globalThis));
